import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Company } from "@/types/company";
import {
  Building2,
  Globe,
  Phone,
  Mail,
  MapPin,
  Users,
  Calendar,
  Clock,
  Activity,
  Trash2,
  Edit,
  ExternalLink,
  Briefcase,
  FileText,
  UserPlus,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CreateContactModal } from "@/components/contacts/CreateContactModal";
import { useContacts } from "@/hooks/useContacts";
import { contactoAPI } from "@/services/contactoService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface CompanyDetailProps {
  company: Company | null;
  isOpen: boolean;
  onClose: () => void;
  onContactClick: (contact: any) => void;
  onUpdate: (id: string, data: Partial<Company>) => void;
  onDelete: (id: string) => void;
}

const getIndustryName = (company: Company): string => {
  if (!company) return "Sin industria";
  if (typeof company.industry === "string") return company.industry;
  if (company.industry && typeof company.industry === "object") {
    const industry = company.industry as any;
    return industry.name || company.industry_id || "Sin industria";
  }
  return company.industry_id || "Sin industria";
};

const safeFormatDate = (
  date: Date | string | undefined | null,
  formatString: string = "dd MMM yyyy",
) => {
  if (!date) return "No disponible";
  try {
    let dateObj: Date;
    if (
      typeof date === "string" &&
      date.includes(" ") &&
      !date.includes("T") &&
      !date.includes("Z")
    ) {
      dateObj = new Date(date.replace(" ", "T") + "Z");
    } else {
      dateObj = typeof date === "string" ? new Date(date) : date;
    }
    if (isNaN(dateObj.getTime())) return "Fecha inválida";
    return format(dateObj, formatString, { locale: es });
  } catch (error) {
    return "Error en fecha";
  }
};

export function CompanyDetail({
  company,
  isOpen,
  onClose,
  onContactClick,
  onUpdate,
  onDelete,
}: CompanyDetailProps) {
  const { toast } = useToast();
  const { company: authCompany } = useAuth();
  const { statuses: customerStatuses, refreshContacts } = useContacts();
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] =
    useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!company) return null;

  const handleCreateContact = async (contact: any) => {
    try {
      setSaving(true);
      const createData = {
        name: contact.name || `${contact.firstName} ${contact.lastName}`.trim(),
        position: contact.position || "",
        phone: contact.phone || "",
        email: contact.email || "",
        client_company_id: company.id,
        contact_status_id: contact.contact_status_id || contact.statusId,
        notes: contact.notes || "",
      };

      await contactoAPI.createContacto(authCompany!.slug, createData);

      toast({
        title: "Contacto creado",
        description: "El contacto ha sido creado exitosamente",
      });

      setIsCreateContactModalOpen(false);
      await refreshContacts();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.body?.error ||
        error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "No se pudo guardar el contacto";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete(company.id);
    setShowDeleteDialog(false);
  };

  const handleEdit = () => {
    onUpdate(company.id, {
      name: company.name,
      industry_id: company.industry_id,
      size: company.size,
      phone: company.phone,
      website: company.website,
      address: company.address,
      logo_url: company.logo_url || "",
      notes: company.notes || "",
      parent_company_id: company.parent_company_id || null,
    });
  };

  const companyIndustryName = getIndustryName(company);

  const getSizeDisplay = (size: string) => {
    if (size === "5001-10000") return "Más de 5000 empleados";

    const match = size.match(/(\d+)\s*-?\s*(\d+)?/);
    if (match) {
      const [, min, max] = match;
      if (max) return `${parseInt(min)}-${parseInt(max)} empleados`;
    }
    return size;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-center space-x-4">
            <Avatar className="h-16 w-16 border-2 border-slate-100">
              <AvatarImage src={company.logo_url || ""} />
              <AvatarFallback className="bg-gradient-to-br from-blue-600 to-purple-600 text-white text-xl font-semibold">
                {company.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <DialogTitle className="text-2xl font-bold text-slate-900">
                {company.name}
              </DialogTitle>
              <DialogDescription className="flex items-center space-x-3 mt-2">
                <div className="flex items-center space-x-2">
                  <Briefcase className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-600">{companyIndustryName}</span>
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  <span className="font-medium text-slate-700">
                    {getSizeDisplay(company.size)}
                  </span>
                </div>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Separator className="my-4" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-lg font-semibold">
                  <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {company.phone && (
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <Phone className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Teléfono</p>
                      <p className="font-medium text-slate-800">
                        {company.phone}
                      </p>
                    </div>
                  </div>
                )}

                {company.website && (
                  <div className="flex items-start space-x-3">
                    <div className="bg-emerald-50 p-2 rounded-lg">
                      <Globe className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Sitio Web</p>
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                      >
                        {company.website.replace(/^https?:\/\//, "")}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                  </div>
                )}

                {company.address && (
                  <div className="flex items-start space-x-3">
                    <div className="bg-amber-50 p-2 rounded-lg">
                      <MapPin className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Dirección</p>
                      <p className="text-slate-700">{company.address}</p>
                    </div>
                  </div>
                )}

                {company.email && (
                  <div className="flex items-start space-x-3">
                    <div className="bg-purple-50 p-2 rounded-lg">
                      <Mail className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">
                        Correo Electrónico
                      </p>
                      <p className="font-medium text-slate-800">
                        {company.email}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {company.notes && (
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center text-lg font-semibold">
                    <FileText className="h-5 w-5 mr-2 text-slate-600" />
                    Notas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {company.notes}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-lg font-semibold">
                  <Activity className="h-5 w-5 mr-2 text-slate-600" />
                  Información del Sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Creada</span>
                    </div>
                    <span className="font-medium text-slate-800">
                      {safeFormatDate(
                        (company as any).created_at ||
                          (company as any).createdAt ||
                          (company as any).fecha_creacion,
                        "dd/MM/yyyy HH:mm",
                      )}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        Actualizada
                      </span>
                    </div>
                    <span className="font-medium text-slate-800">
                      {safeFormatDate(
                        (company as any).updated_at ||
                          (company as any).updatedAt ||
                          (company as any).fecha_actualizacion,
                        "dd/MM/yyyy HH:mm",
                      )}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Activity className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        Última Actividad
                      </span>
                    </div>
                    <span className="font-medium text-slate-800">
                      {safeFormatDate(
                        (company as any).last_activity_at ||
                          (company as any).lastActivityAt,
                        "dd/MM/yyyy HH:mm",
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {company.parent_company && (
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center text-lg font-semibold">
                    <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                    Empresa Matriz
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={company.parent_company.logo_url || ""}
                      />
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {company.parent_company.name.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-slate-900">
                        {company.parent_company.name}
                      </p>
                      <p className="text-sm text-slate-600">
                        {getIndustryName(company.parent_company)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {company.subsidiaries && company.subsidiaries.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center text-lg font-semibold">
                    <Users className="h-5 w-5 mr-2 text-blue-600" />
                    Subsidiarias ({company.subsidiaries.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {company.subsidiaries.slice(0, 3).map((subsidiary) => (
                      <div
                        key={subsidiary.id}
                        className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => onContactClick(subsidiary)}
                      >
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={subsidiary.logo_url || ""} />
                            <AvatarFallback className="bg-slate-100 text-slate-700 text-xs">
                              {subsidiary.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm text-slate-900">
                              {subsidiary.name}
                            </p>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-slate-500">
                                {getIndustryName(subsidiary)}
                              </span>
                              {subsidiary.size && (
                                <>
                                  <span className="text-xs text-slate-300">
                                    •
                                  </span>
                                  <span className="text-xs text-slate-600">
                                    {subsidiary.size}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {company.subsidiaries.length > 3 && (
                      <p className="text-sm text-slate-500 text-center pt-2">
                        +{company.subsidiaries.length - 3} más subsidiarias
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <Separator className="my-6" />
        <div className="flex justify-between">
          <Button
            variant="destructive"
            onClick={handleDeleteClick}
            className="flex items-center px-4"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </Button>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsCreateContactModalOpen(true)}
              className="px-4 border-green-600 text-green-600 hover:bg-green-50"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Crear Contacto
            </Button>
            <Button
              onClick={() => onUpdate(company.id, company)}
              className="px-4 bg-blue-600 hover:bg-blue-700"
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button variant="outline" onClick={onClose} className="px-4">
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>

      <CreateContactModal
        isOpen={isCreateContactModalOpen}
        onClose={() => setIsCreateContactModalOpen(false)}
        onSave={handleCreateContact}
        existingContact={{ companyId: company.id }}
        customerStatuses={customerStatuses}
        existingContacts={[]}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la empresa "{company.name}" y todos sus datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
